import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { Download, Trash2, Package, FileText, User2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AccountPage() {
  const t = useT();
  const { user, formatPrice } = useApp();
  const [tab, setTab] = useState("orders");
  const [quotes, setQuotes] = useState([]);
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/quotes").then((r) => setQuotes(r.data));
    api.get("/me/configurations").then((r) => setConfigs(r.data));
  }, [user]);

  if (!user) {
    return (
      <div className="container-x py-20 text-center">
        <h1 className="display text-3xl font-semibold">Log in om je account te zien</h1>
        <Link to="/login" className="btn-primary mt-6 inline-flex">Inloggen</Link>
      </div>
    );
  }

  const deleteCfg = async (id) => {
    await api.delete(`/me/configurations/${id}`);
    setConfigs(configs.filter((c) => c.id !== id));
  };

  return (
    <div className="container-x py-12" data-testid="account-page">
      <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.account.title}</h1>

      <div className="grid lg:grid-cols-12 gap-8 mt-8">
        <aside className="lg:col-span-3">
          <nav className="card-surface p-3 space-y-1">
            <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={<FileText className="w-4 h-4" />} testId="tab-orders">{t.account.orders}</TabBtn>
            <TabBtn active={tab === "configs"} onClick={() => setTab("configs")} icon={<Package className="w-4 h-4" />} testId="tab-configs">{t.account.configs}</TabBtn>
            <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} icon={<User2 className="w-4 h-4" />} testId="tab-profile">{t.account.profile}</TabBtn>
          </nav>
        </aside>

        <div className="lg:col-span-9 space-y-4">
          {tab === "orders" && (
            <>
              {quotes.length === 0 && <div className="card-surface p-10 text-center text-[#A8B3AD]">{t.account.no_orders}</div>}
              {quotes.map((q) => (
                <div key={q.id} className="card-surface p-5 flex items-center gap-4" data-testid={`order-row-${q.quote_number}`}>
                  <div className="flex-1">
                    <div className="font-semibold">{q.quote_number}</div>
                    <div className="text-xs text-[#A8B3AD]">{new Date(q.created_at).toLocaleDateString()} · {q.items.length} regels</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[#6FAA8A]">{formatPrice(q.subtotal_once)}</div>
                    <div className="text-xs text-[#D97742]">{formatPrice(q.subtotal_monthly)}/mnd</div>
                  </div>
                  <a href={`${API}/quotes/${q.id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline !py-2" data-testid={`download-${q.quote_number}`}>
                    <Download className="w-4 h-4" /> PDF
                  </a>
                </div>
              ))}
            </>
          )}

          {tab === "configs" && (
            <>
              {configs.length === 0 && <div className="card-surface p-10 text-center text-[#A8B3AD]">{t.account.no_configs}</div>}
              {configs.map((c) => (
                <div key={c.id} className="card-surface p-5 flex items-center gap-4" data-testid={`config-row-${c.id}`}>
                  <div className="flex-1">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-[#A8B3AD]">{c.sector_slug} · {c.usecase_slug} · {c.items.length} items</div>
                  </div>
                  <button className="btn-ghost text-[#D97742]" onClick={() => deleteCfg(c.id)} data-testid={`delete-config-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </>
          )}

          {tab === "profile" && (
            <div className="card-surface p-6" data-testid="profile-panel">
              <Row k="Naam" v={user.name} />
              <Row k="E-mail" v={user.email} />
              <Row k="Bedrijf" v={user.company || "-"} />
              <Row k="Rol" v={user.role} />
              <Row k="E-mail bevestigd" v={user.email_verified ? "Ja ✓" : "Nee, check verificatie mail"} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, icon, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition ${active ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD] hover:bg-[#1A2421]"}`}>
    {icon} {children}
  </button>
);

const Row = ({ k, v }) => (
  <div className="flex py-2.5 border-b last:border-0 border-[#243430] text-sm">
    <div className="w-40 text-[#A8B3AD]">{k}</div>
    <div className="flex-1 font-medium text-[#F4F1EA]">{v}</div>
  </div>
);
